import { labeebAuditRow, labeebPaged, labeebTenantHistoryPath, type LabeebAuditEvent } from "../../api/labeeb";
import type { AppConfig } from "../../config";
import { labeebPage, usePagedList } from "../../hooks/usePagedList";
import { AuditEventTable } from "../audit/AuditEventTable";
import { LoadMore } from "../LoadMore";
import { ResourceView } from "../states";
import { Empty } from "../ui";

/**
 * Changes made to one business through Labeeb's admin API.
 *
 * Uses the same table as Governance → Audit. The feed is
 * `GET /admin/tenant/{id}/history` (this merchant only); Governance reads the
 * fleet list with filters — same row shape, different query.
 */
export function LabeebHistory({
  config,
  tenantId,
  preview,
}: {
  config: AppConfig;
  tenantId: string;
  /** Show only the first rows. The history page omits this and pages. */
  preview?: number;
}) {
  const history = usePagedList<LabeebAuditEvent>(
    config,
    labeebPaged(labeebTenantHistoryPath(tenantId)),
    labeebPage,
  );

  return (
    <ResourceView state={history.state} what="change history" onRetry={history.reload}>
      {(page) => {
        const slice = preview === undefined ? page.rows : page.rows.slice(0, preview);
        const rows = slice.map(labeebAuditRow);
        if (rows.length === 0) {
          return (
            <>
              <Empty>
                {page.next
                  ? "No changes on this page."
                  : "No changes recorded for this business."}
              </Empty>
              {preview === undefined && (
                <LoadMore
                  shown={0}
                  noun="changes"
                  hasMore={page.next !== null}
                  loading={history.loadingMore}
                  error={history.moreError}
                  onMore={history.loadMore}
                />
              )}
            </>
          );
        }
        return (
          <>
            <AuditEventTable rows={rows} product="labeeb" />
            {preview === undefined && (
              <LoadMore
                shown={page.rows.length}
                noun="changes"
                hasMore={page.next !== null}
                loading={history.loadingMore}
                error={history.moreError}
                onMore={history.loadMore}
              />
            )}
          </>
        );
      }}
    </ResourceView>
  );
}
